import { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import { login as loginApi, verifySession } from '../services/api';

const AuthContext = createContext(null);

// How often to check if the user is still active (ms).
// 30 seconds balances responsiveness vs. API noise.
const VERIFY_INTERVAL_MS = 30_000;

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);
  const intervalRef           = useRef(null);

  // ── Core logout — clears storage and state ────────────────────────────────
  const logout = useCallback(() => {
    localStorage.removeItem('token');   localStorage.removeItem('user');
    sessionStorage.removeItem('token'); sessionStorage.removeItem('user');
    setUser(null);
    stopPolling();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Session polling ───────────────────────────────────────────────────────
  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const checkSession = useCallback(async () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) return;
    try {
      const res = await verifySession();
      if (!res.data.valid) {
        // Account deactivated, archived, or deleted — force logout
        logout();
      }
    } catch (err) {
      // 401 means JWT expired or invalid — force logout
      // Network errors are ignored (don't kick out an offline user)
      if (err.response?.status === 401) {
        logout();
      }
    }
  }, [logout]);

  const startPolling = useCallback(() => {
    stopPolling();
    intervalRef.current = setInterval(checkSession, VERIFY_INTERVAL_MS);
  }, [checkSession]);

  // ── Restore session on app load ───────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('token')   || sessionStorage.getItem('token');
    const saved = localStorage.getItem('user')    || sessionStorage.getItem('user');

    if (token && saved) {
      try {
        setUser(JSON.parse(saved));
        // Verify immediately on load, then start the interval
        setTimeout(checkSession, 1000);
        startPolling();
      } catch {
        logout();
      }
    }
    setLoading(false);

    return () => stopPolling();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Login ─────────────────────────────────────────────────────────────────
  const login = async (username, password, rememberMe = false) => {
    const response = await loginApi(username, password);
    const { token, ...userData } = response.data;

    localStorage.removeItem('token');   localStorage.removeItem('user');
    sessionStorage.removeItem('token'); sessionStorage.removeItem('user');
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));

    setUser(userData);
    startPolling(); // begin session checks after successful login
    return userData;
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// Export for SuperadminPanel impersonation
export { AuthContext };